import { Menu, type MenuItem } from "../../components/ui/Menu";
import { Status } from "../../components/ui/Status";
import { useI18n } from "../../i18n/I18nProvider";
import type { Invite } from "../../lib/api-types";
import { formatDate } from "../api-keys/format-date";
import { inviteStatus } from "./invite-status";

export interface PendingInviteRowProps {
  invite: Invite;
  busy: boolean;
  onResend: () => void;
  onRevoke: () => void;
}

/** A pending invitation: email, role, expiry and status, with Resend and Revoke in a menu. */
export function PendingInviteRow({ invite, busy, onResend, onRevoke }: PendingInviteRowProps) {
  const { t, lang } = useI18n();
  const expired = inviteStatus(invite) === "expired";
  const items: MenuItem[] = [
    { label: t("members.resend"), onSelect: onResend, disabled: busy },
    { label: t("members.revoke"), onSelect: onRevoke, tone: "danger", disabled: busy },
  ];
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-operate font-semibold text-ink">{invite.email}</p>
        <p className="text-note text-ink-2">
          {t(invite.role === "owner" ? "common.role.owner" : "common.role.member")} ·{" "}
          {t("members.expires", { date: formatDate(invite.expiresAt, lang) })}
        </p>
      </div>
      {expired ? (
        <Status tone="idle">{t("members.inviteStatusExpired")}</Status>
      ) : (
        <Status tone="linked">{t("members.inviteStatusPending")}</Status>
      )}
      <Menu label={t("members.moreActions", { name: invite.email })} items={items} />
    </li>
  );
}
